import { useEffect,useMemo,useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TeamDelegatesTable from "./AdminTeamDelegates/TeamDelegatesTable";
import TeamDelegateModal from "./AdminTeamDelegates/TeamDelegateModal";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function AdminTeamDelegates(){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [delegates,setDelegates]=useState([]);
  const [temporaryPasswords,setTemporaryPasswords]=useState({});
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedDelegate,setSelectedDelegate]=useState(null);
  const [passwordDialogOpen,setPasswordDialogOpen]=useState(false);
  const [passwordDelegate,setPasswordDelegate]=useState(null);
  const [generatedPassword,setGeneratedPassword]=useState("");
  const [generatingPassword,setGeneratingPassword]=useState(false);

  const loadDelegates=async()=>{
    try{
      setLoading(true);

      const response=await fetch("/api/delegates/",{
        credentials:"include",
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudieron cargar los delegados.");
      }

      setDelegates(Array.isArray(data)?data:[]);
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadDelegates();
  },[]);

  const filteredDelegates=useMemo(()=>{
    const value=search.trim().toLowerCase();

    if(!value) return delegates;

    return delegates.filter((delegate)=>
      [
        delegate.nombre_completo,
        delegate.nombres,
        delegate.apellidos,
        delegate.identificacion,
        delegate.correo,
        delegate.telefono,
      ].some((field)=>String(field||"").toLowerCase().includes(value))
    );
  },[delegates,search]);

  const handleCreate=()=>{
    setSelectedDelegate(null);
    setModalOpen(true);
  };

  const handleEdit=(delegate)=>{
    setSelectedDelegate(delegate);
    setModalOpen(true);
  };

  const handleSaved=async(data)=>{
    if(data?.temporary_password&&data?.identificacion){
      setTemporaryPasswords((current)=>({
        ...current,
        [data.identificacion]:data.temporary_password,
      }));

      setPasswordDelegate(data);
      setGeneratedPassword(data.temporary_password);
      setPasswordDialogOpen(true);
    }

    setModalOpen(false);
    setSelectedDelegate(null);
    await loadDelegates();
  };

  const handleGeneratePassword=async(delegate)=>{
    if(!delegate?.identificacion) return;

    try{
      setGeneratingPassword(true);

      const response=await fetch(
        `/api/delegates/${encodeURIComponent(delegate.identificacion)}/reset-password/`,
        {
          method:"POST",
          credentials:"include",
          headers:{
            "X-CSRFToken":getCookie("csrftoken"),
          },
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudo generar la clave temporal.");
      }

      setTemporaryPasswords((current)=>({
        ...current,
        [delegate.identificacion]:data.temporary_password,
      }));

      setPasswordDelegate(delegate);
      setGeneratedPassword(data.temporary_password||"");
      setPasswordDialogOpen(true);

      await loadDelegates();
      showSuccess("Clave temporal generada correctamente.");
    }catch(error){
      showError(error.message);
    }finally{
      setGeneratingPassword(false);
    }
  };

  const normalizeWhatsappPhone=(phone)=>{
    let value=String(phone||"").replace(/\D/g,"");

    if(value.startsWith("593")) return value;
    if(value.startsWith("0")) value=value.substring(1);

    return `593${value}`;
  };

  const handleWhatsApp=(delegate)=>{
    const temporaryPassword=temporaryPasswords[delegate.identificacion];

    if(!temporaryPassword){
      showError("Primero genera una clave temporal para este delegado.");
      return;
    }

    if(!delegate.telefono){
      showError("El delegado no tiene un teléfono registrado.");
      return;
    }

    const phone=normalizeWhatsappPhone(delegate.telefono);
    const name=delegate.nombre_completo||delegate.nombres||"";
    const message=[
      `Hola ${name}.`,
      "",
      "Se ha creado tu acceso al sistema de torneos.",
      `Usuario: ${delegate.correo}`,
      `Clave temporal: ${temporaryPassword}`,
      "",
      "Al ingresar deberás actualizar tu contraseña.",
    ].join("\n");

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleClosePasswordDialog=()=>{
    setPasswordDialogOpen(false);
    setPasswordDelegate(null);
    setGeneratedPassword("");
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Delegados de equipos
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary,
            }}
          >
            Administra las cuentas de los delegados de los equipos.
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          onClick={handleCreate}
          sx={{
            px:2.2,
            backgroundColor:colors.secondary,
            color:colors.tableHeaderText,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          Nuevo delegado
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          display:"flex",
          justifyContent:"flex-end",
        }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(event)=>setSearch(event.target.value)}
          placeholder="Buscar delegado"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface,
            },
          }}
        />
      </Box>

      {loading?(
        <Box
          sx={{
            minHeight:220,
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radiusLarge,
            backgroundColor:colors.surface,
          }}
        >
          <CircularProgress size={24} sx={{color:colors.secondary}} />
        </Box>
      ):(
        <TeamDelegatesTable
          delegates={filteredDelegates}
          temporaryPasswords={temporaryPasswords}
          onEdit={handleEdit}
          onGeneratePassword={handleGeneratePassword}
          onWhatsApp={handleWhatsApp}
          generatingPassword={generatingPassword}
        />
      )}

      <TeamDelegateModal
        open={modalOpen}
        onClose={()=>{
          setModalOpen(false);
          setSelectedDelegate(null);
        }}
        onSaved={handleSaved}
        delegate={selectedDelegate}
      />

      <Dialog
        open={passwordDialogOpen}
        onClose={handleClosePasswordDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
          },
        }}
      >
        <DialogTitle sx={{pb:1}}>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Clave temporal
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              mb:1.5,
              color:colors.textSecondary,
              fontSize:typography.sizes.sm,
            }}
          >
            Esta clave se muestra únicamente en esta sesión. Puedes enviarla por WhatsApp cuando lo desees mientras permanezca disponible.
          </Typography>

          <TextField
            size="small"
            label="Correo"
            value={passwordDelegate?.correo||""}
            fullWidth
            InputProps={{readOnly:true}}
            sx={{mb:1.5}}
          />

          <TextField
            size="small"
            label="Clave temporal"
            value={generatedPassword}
            fullWidth
            InputProps={{readOnly:true}}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px:3,
            py:2,
            borderTop:`1px solid ${colors.border}`,
            gap:1,
          }}
        >
          <Button
            size="small"
            onClick={handleClosePasswordDialog}
            sx={{color:colors.textSecondary}}
          >
            Cerrar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={()=>{
              if(passwordDelegate) handleWhatsApp(passwordDelegate);
            }}
            sx={{
              backgroundColor:colors.secondary,
              color:colors.tableHeaderText,
              "&:hover":{
                backgroundColor:colors.secondaryHover,
              },
            }}
          >
            Enviar por WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}